import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentUser } from 'FrontRoyalAngular';
import { getCohort } from 'Users';
import { type PlaceDetails } from 'GooglePlaces';
import { type StudentNetworkProfileFilters } from '../api/studentNetworkV2Api.types';
import { type FilterTab, type FilterState } from './usePeopleFilters.types';
import { hasActiveFiltersInState } from './usePeopleFilters.utils';
import { peopleFiltersActions } from '../peopleFiltersSlice';

const DEBOUNCE_MS = 250;

function buildApiFilters(
    state: FilterState,
    debouncedKeyword: string,
    cohortId?: string,
): StudentNetworkProfileFilters {
    const filters: StudentNetworkProfileFilters = {};

    // Keyword search — companies are merged into the keyword_search param rather
    // than sent as a separate filter because the API uses websearch_to_tsquery
    // for full-text search. Quoting each company and joining with OR gives the
    // desired "any of these companies" union behavior.
    const companyExpr = state.companies.length > 0 ? state.companies.map(c => `"${c}"`).join(' OR ') : '';
    const searchTerms = [debouncedKeyword, companyExpr].filter(Boolean).join(' ');
    if (searchTerms) filters.keyword_search = encodeURIComponent(searchTerms);

    // Tab-based filters
    if (state.activeTab === 'alumni') filters.alumni = true;
    if (state.activeTab === 'students') filters.alumni = false;
    if (state.activeTab === 'myClass' && cohortId) filters.cohort_id = cohortId;

    // Places (city search)
    if (state.places.length > 0) filters.places = state.places as Record<string, unknown>[];

    // Program types
    if (state.programTypes.length > 0) filters.program_type = state.programTypes as unknown as string;

    // Industries
    if (state.industries.length > 0) filters.industries = state.industries;

    // Interests are URL-encoded individually (unlike other array filters) because
    // the v1 API expects each interest value to be pre-encoded in the request.
    if (state.interests.length > 0) {
        filters.student_network_interests = state.interests.map(i => encodeURIComponent(i));
    }

    // Looking for
    if (state.lookingFor.length > 0) filters.student_network_looking_for = state.lookingFor;

    // Timezones
    if (state.timezones.length > 0) filters.timezones = state.timezones;

    // User IDs (hidden filter from ?ids= query param)
    if (state.userIds.length > 0) filters.user_id = state.userIds;

    return filters;
}

type PeopleFiltersState = { peopleFilters: FilterState };

// oxlint-disable-next-line max-lines-per-function -- filter hook with many fields
export function usePeopleFilters({
    initialUserIds,
    initialMyClass,
}: { initialUserIds?: string[]; initialMyClass?: boolean } = {}) {
    const dispatch = useDispatch();
    const currentUser = useCurrentUser();
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const state = useSelector((s: PeopleFiltersState) => s.peopleFilters);

    // Apply initial user IDs from ?ids= query param on first mount
    const initialAppliedRef = useRef(false);
    useEffect(() => {
        if (initialUserIds?.length && !initialAppliedRef.current) {
            initialAppliedRef.current = true;
            dispatch(peopleFiltersActions.setUserIds(initialUserIds));
        }
    }, [initialUserIds, dispatch]);

    // Apply My Class tab from ?myClass query param on first mount
    const myClassAppliedRef = useRef(false);
    useEffect(() => {
        if (initialMyClass && !myClassAppliedRef.current) {
            myClassAppliedRef.current = true;
            dispatch(peopleFiltersActions.resetFilters());
            dispatch(peopleFiltersActions.setActiveTab('myClass'));
        }
    }, [initialMyClass, dispatch]);

    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const keywordDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Debounce keyword search
    useEffect(() => {
        if (keywordDebounceRef.current) clearTimeout(keywordDebounceRef.current);
        keywordDebounceRef.current = setTimeout(() => setDebouncedKeyword(state.keywordSearch), DEBOUNCE_MS);
        return () => {
            if (keywordDebounceRef.current) clearTimeout(keywordDebounceRef.current);
        };
    }, [state.keywordSearch]);

    const filters = useMemo(
        () => buildApiFilters(state, debouncedKeyword, cohort?.id),
        [state, debouncedKeyword, cohort?.id],
    );

    // Setters — dispatch Redux actions
    const setKeywordSearch = useCallback(
        (value: string) => dispatch(peopleFiltersActions.setKeywordSearch(value)),
        [dispatch],
    );
    const setActiveTab = useCallback((tab: FilterTab) => dispatch(peopleFiltersActions.setActiveTab(tab)), [dispatch]);
    const setShowAdvanced = useCallback(
        (show: boolean) => dispatch(peopleFiltersActions.setShowAdvanced(show)),
        [dispatch],
    );
    const setPlaces = useCallback(
        (places: PlaceDetails[]) => dispatch(peopleFiltersActions.setPlaces(places)),
        [dispatch],
    );
    const setProgramTypes = useCallback(
        (programTypes: string[]) => dispatch(peopleFiltersActions.setProgramTypes(programTypes)),
        [dispatch],
    );
    const setCompanies = useCallback(
        (companies: string[]) => dispatch(peopleFiltersActions.setCompanies(companies)),
        [dispatch],
    );
    const setIndustries = useCallback(
        (industries: string[]) => dispatch(peopleFiltersActions.setIndustries(industries)),
        [dispatch],
    );
    const setInterests = useCallback(
        (interests: string[]) => dispatch(peopleFiltersActions.setInterests(interests)),
        [dispatch],
    );
    const setLookingFor = useCallback(
        (lookingFor: string[]) => dispatch(peopleFiltersActions.setLookingFor(lookingFor)),
        [dispatch],
    );
    const setTimezones = useCallback(
        (timezones: string[]) => dispatch(peopleFiltersActions.setTimezones(timezones)),
        [dispatch],
    );

    // Bulk apply — used by mobile filters screen
    const applyFilterState = useCallback(
        (newState: FilterState) => {
            dispatch(peopleFiltersActions.setKeywordSearch(newState.keywordSearch));
            dispatch(peopleFiltersActions.setActiveTab(newState.activeTab));
            dispatch(peopleFiltersActions.setShowAdvanced(newState.showAdvanced));
            dispatch(peopleFiltersActions.setPlaces(newState.places));
            dispatch(peopleFiltersActions.setProgramTypes(newState.programTypes));
            dispatch(peopleFiltersActions.setCompanies(newState.companies));
            dispatch(peopleFiltersActions.setIndustries(newState.industries));
            dispatch(peopleFiltersActions.setInterests(newState.interests));
            dispatch(peopleFiltersActions.setLookingFor(newState.lookingFor));
            dispatch(peopleFiltersActions.setTimezones(newState.timezones));
            dispatch(peopleFiltersActions.setUserIds(newState.userIds));
        },
        [dispatch],
    );

    // Reset all filters to initial state
    const resetFilters = useCallback(() => dispatch(peopleFiltersActions.resetFilters()), [dispatch]);

    const activeFilterCount = useMemo(
        () =>
            [
                state.activeTab !== 'all',
                state.places.length > 0,
                state.programTypes.length > 0,
                state.companies.length > 0,
                state.industries.length > 0,
                state.interests.length > 0,
                state.lookingFor.length > 0,
                state.timezones.length > 0,
                state.userIds.length > 0,
            ].filter(Boolean).length,
        [state],
    );

    const hasActiveFilters = hasActiveFiltersInState(state);

    return {
        filters,
        filterState: state,
        applyFilterState,
        hasActiveFilters,
        activeFilterCount,
        resetFilters,
        keywordSearch: state.keywordSearch,
        setKeywordSearch,
        activeTab: state.activeTab,
        setActiveTab,
        showAdvanced: state.showAdvanced,
        setShowAdvanced,
        places: state.places,
        setPlaces,
        programTypes: state.programTypes,
        setProgramTypes,
        companies: state.companies,
        setCompanies,
        industries: state.industries,
        setIndustries,
        interests: state.interests,
        setInterests,
        lookingFor: state.lookingFor,
        setLookingFor,
        timezones: state.timezones,
        setTimezones,
    };
}
